/**
 * Custom error class for authentication failures (401, 403, token expired, etc.)
 * When this error is thrown, the application should redirect the user to the login page.
 */
export class AuthenticationError extends Error {
  /**
   * HTTP status code of the failed request
   */
  public readonly statusCode: number;

  /**
   * Creates a new AuthenticationError
   * @param params - Error parameters
   * @param params.message - Human-readable error message
   * @param params.statusCode - HTTP status code (typically 401 or 403)
   */
  constructor({ message, statusCode }: { message: string; statusCode: number }) {
    super(message);
    this.name = "AuthenticationError";
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

/**
 * Custom error class for backend and network failures
 * This includes server errors (5xx), network timeouts, and other non-authentication issues.
 */
export class BackendError extends Error {
  /**
   * HTTP status code of the failed request (if available)
   */
  public readonly statusCode?: number;

  /**
   * Machine-readable reason the server gave for refusing the request, when it
   * sent one. Carried separately from the message so a caller can map it to
   * its own wording instead of showing transport text.
   */
  public readonly reason?: string;

  /**
   * The contract's machine-readable error code (e.g. `AGREEMENT_NOT_ACCEPTED`),
   * when the response body carried one. The only field a caller may branch on.
   */
  public readonly code?: string;

  /**
   * The sentence the server wrote for the person using the app, when it sent
   * one. Kept apart from `message`, which carries transport wording no caller
   * may put on screen.
   */
  public readonly serverMessage?: string;

  /**
   * How long the server asked a refused caller to wait, in seconds, when it
   * said. Only a rate limit answers with one.
   */
  public readonly retryAfterSeconds?: number;

  /**
   * Creates a new BackendError
   * @param params - Error parameters
   * @param params.message - Human-readable error message
   * @param params.statusCode - Optional HTTP status code
   * @param params.reason - Optional reason code from the response body
   * @param params.code - Optional contract error code from the response body
   * @param params.serverMessage - Optional participant-facing sentence from the response body
   * @param params.retryAfterSeconds - Optional wait the server asked for
   */
  constructor({
    message,
    statusCode,
    reason,
    code,
    serverMessage,
    retryAfterSeconds,
  }: {
    message: string;
    statusCode?: number;
    reason?: string;
    code?: string;
    serverMessage?: string;
    retryAfterSeconds?: number;
  }) {
    super(message);
    this.name = "BackendError";
    this.statusCode = statusCode;
    this.reason = reason;
    this.code = code;
    this.serverMessage = serverMessage;
    this.retryAfterSeconds = retryAfterSeconds;
    Object.setPrototypeOf(this, BackendError.prototype);
  }
}

/**
 * Helper to check if the error is an authentication-related error.
 * Handles both custom AuthenticationError and Auth0's "Login required" error.
 * @param err - The error to check
 * @returns True if the error is authentication-related
 */
export const isAuthenticationError = (err: unknown): boolean => {
  return err instanceof AuthenticationError || (err instanceof Error && err.message.includes("Login required"));
};

/**
 * Helper to check if the error is a chunk load error (typically occurs after deployment).
 * This happens when:
 * 1. A new version is deployed with different chunk hashes
 * 2. Users have the old index.html cached
 * 3. Old chunks no longer exist, server returns HTML instead of JS
 * 4. Browser throws ChunkLoadError because HTML can't be executed as JavaScript
 * @param err - The error to check
 * @returns True if the error is a chunk load error
 */
export const isChunkLoadError = (err: unknown): boolean => {
  if (!(err instanceof Error)) {
    return false;
  }

  const errorName = err.name?.toLowerCase() ?? "";
  const errorMessage = err.message?.toLowerCase() ?? "";

  return (
    errorName === "chunkloaderror" ||
    errorMessage.includes("loading chunk") ||
    errorMessage.includes("failed to fetch dynamically imported module") ||
    (errorMessage.includes("mime type") && errorMessage.includes("text/html"))
  );
};
